import { useState, useRef, useEffect } from 'react'
import { Shield, Mail, Phone, MapPin, Send, Check, MessageSquare, MessageCircle, Instagram, Music } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

function useInView(threshold = 0.15) {
  const ref = useRef(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true)
    }, { threshold })
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, inView]
}

function FadeIn({ children, delay = 0, className = '' }) {
  const [ref, inView] = useInView()
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
    }}>{children}</div>
  )
}

export default function Contact({ onNavigate, onGetStarted }) {
  const { company } = useAuth()
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const { error: insertError } = await supabase
        .from('contact_messages')
        .insert({
          name: form.name,
          email: form.email,
          subject: form.subject,
          message: form.message,
          company_id: company?.id || null,
        })
      if (insertError) throw insertError
      setSubmitted(true)
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-ink rounded-lg flex items-center justify-center">
              <Shield size={18} className="text-white" />
            </div>
            <span className="font-bold text-lg text-ink">Bizos Suite</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => onNavigate('landing')} className="text-sm font-medium text-neutral-700 hover:text-brand">Home</button>
            <button onClick={() => onNavigate('about')} className="text-sm font-medium text-neutral-700 hover:text-brand">About</button>
            <button onClick={() => onNavigate('pricing')} className="text-sm font-medium text-neutral-700 hover:text-brand">Pricing</button>
            <button onClick={onGetStarted} className="text-sm font-semibold text-brand">Sign In</button>
            <button onClick={() => onNavigate('pricing')} className="btn-primary text-sm">Get Started</button>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-neutral-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-brand-soft text-brand text-xs font-semibold rounded-full mb-6">
              <MessageSquare size={12} /> Get in Touch
            </span>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1 className="text-4xl md:text-5xl font-bold text-ink leading-tight tracking-tight">Let's talk about your governance needs</h1>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-6 text-lg text-neutral-600">Whether you're preparing for due diligence, need help with compliance, or want to learn more — we're here.</p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-24 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-12">
          <FadeIn className="lg:col-span-2">
            <div className="space-y-6">
              <div className="p-6 rounded-2xl border border-neutral-200">
                <div className="w-10 h-10 bg-brand-soft rounded-lg flex items-center justify-center mb-3">
                  <Mail size={18} className="text-brand" />
                </div>
                <h3 className="text-sm font-semibold text-ink">Email</h3>
                <p className="mt-1 text-sm text-neutral-600">bizoshigroups@gmail.com</p>
              </div>
              <div className="p-6 rounded-2xl border border-neutral-200">
                <div className="w-10 h-10 bg-brand-soft rounded-lg flex items-center justify-center mb-3">
                  <Phone size={18} className="text-brand" />
                </div>
                <h3 className="text-sm font-semibold text-ink">Phone</h3>
                <p className="mt-1 text-sm text-neutral-600">08142243764</p>
                <p className="text-sm text-neutral-600">Mon–Fri, 9am–6pm WAT</p>
              </div>
              <div className="p-6 rounded-2xl border border-neutral-200">
                <div className="w-10 h-10 bg-brand-soft rounded-lg flex items-center justify-center mb-3">
                  <MapPin size={18} className="text-brand" />
                </div>
                <h3 className="text-sm font-semibold text-ink">Office</h3>
                <p className="mt-1 text-sm text-neutral-600">Victoria Island</p>
                <p className="text-sm text-neutral-600">Lagos, Nigeria</p>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} className="lg:col-span-3">
            <div className="p-8 rounded-2xl border border-neutral-200">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <Check size={32} className="text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-ink">Message sent!</h3>
                  <p className="mt-2 text-sm text-neutral-600">We'll get back to you within 24 hours.</p>
                  <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }) }} className="btn-secondary mt-6">
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="label-above">Full Name</label>
                      <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-flat" placeholder="Your name" />
                    </div>
                    <div>
                      <label className="label-above">Email</label>
                      <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-flat" placeholder="you@company.com" />
                    </div>
                  </div>
                  <div>
                    <label className="label-above">Subject</label>
                    <input type="text" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="input-flat" placeholder="How can we help?" />
                  </div>
                  <div>
                    <label className="label-above">Message</label>
                    <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-flat resize-none" placeholder="Tell us more..." />
                  </div>
                  <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                    {loading ? 'Sending...' : <>Send Message <Send size={16} /></>}
                  </button>
                  {error && <p className="text-sm text-rose-600 text-center">{error}</p>}
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      <footer className="py-12 px-6 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-ink rounded-lg flex items-center justify-center">
              <Shield size={18} className="text-white" />
            </div>
            <span className="font-bold text-ink">Bizos Suite</span>
          </div>
          <div className="flex flex-wrap gap-4 mb-4">
            <a href="tel:08142243764" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <Phone size={14} /> 08142243764
            </a>
            <a href="https://wa.me/2348142243764" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <MessageCircle size={14} /> WhatsApp
            </a>
            <a href="https://instagram.com/officialbizos" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <Instagram size={14} /> @officialbizos
            </a>
            <a href="https://tiktok.com/@officialbizos" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand">
              <Music size={14} /> @officialbizos
            </a>
          </div>
          <p className="text-xs text-neutral-400">© 2026 Bizos Suite. All rights reserved. Built in Nigeria for Africa. Powered by Vobels Limited.</p>
        </div>
      </footer>
    </div>
  )
}
